import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ProjectTeam } from './models';
import { baseApi } from '@centerpiece/components';

export const project_team = baseApi
  .enhanceEndpoints({ addTagTypes: ['ProjectTeams', 'ProjectTeam'] })
  .injectEndpoints({
    endpoints: builder => ({
      getProjectTeams: builder.query<ProjectTeam[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'project_team',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'ProjectTeam' as const,
                  id,
                })),
                'ProjectTeams',
              ]
            : ['ProjectTeams'],
      }),
      getProjectTeam: builder.query<ProjectTeam, string>({
        query: id => ({
          url: `project_team/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'ProjectTeam', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createProjectTeam: builder.mutation({
        query: request => ({
          url: 'project_team',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['ProjectTeams'],
      }),
      updateProjectTeam: builder.mutation<ProjectTeam, ProjectTeam>({
        query: request => ({
          url: `project_team/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'ProjectTeam', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            project_team.util.updateQueryData(
              'getProjectTeam',
              `${id}`,
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(project_team.util.invalidateTags(['ProjectTeams']))
             */
            dispatch(project_team.util.invalidateTags(['ProjectTeams']));
          }
        },
      }),
      deleteProjectTeam: builder.mutation<ProjectTeam, number>({
        query: id => ({
          url: `project_team/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'ProjectTeam' as const, arg }, 'ProjectTeams']
            : ['ProjectTeams'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetProjectTeamsQuery,
  useGetProjectTeamQuery,
  useCreateProjectTeamMutation,
  useUpdateProjectTeamMutation,
  useDeleteProjectTeamMutation,
  usePrefetch: useProjectTeamsPrefetch,
} = project_team;
const selectProjectTeamsRoot = project_team.endpoints.getProjectTeams.select(
  {}
);
export const selectProjectTeams = createSelector(
  selectProjectTeamsRoot,
  project_team => project_team.data || []
);
